import styles from 'src/pages/upgrades/UpgradesView.module.scss';
import { Breadcrumbs, SkeletonText } from 'subframe/index';
import Page from 'components/Page';
import useAccountIdRoute from 'hooks/useAccountIdRoute';
import { useNavigate, useParams } from 'react-router';
import { useSnackbar } from 'notistack';
import useUserAccountState from 'hooks/useUserAccountState';
import { useSWRConfig } from 'swr';
import {
  isExampleData,
  UPGRADE_ASSESSMENTS,
  UPGRADE_STAGES,
} from 'src/data/upgrades';
import {
  updateUpgradeAssessment,
  useGetUpgradeAssessment,
  useListSubscriptions,
  useListUpgradeAssessmentStages,
} from 'api/frontend';
import { somethingWentWrong, toastAutoHideDuration } from 'constants/toasts';
import { ConsoleLoader } from 'components/Loader';
import { UpgradesViewComponent } from '../UpgradesView';
import { UpgradeViewMode } from '../helpers/UpgradesHelpers';
import { getHumanReadableDateAndTime } from 'utils/formatTime';
import classNames from 'classnames';
import { Actions } from 'components/design-system';
import { useState } from 'react';
import { ChangeNameDialog } from '../helpers/ChangeNameDialog';
import AnalyticsEventLogger from 'utils/AnalyticsEventLogger';
import { UpgradeAssessment } from 'api/models/upgradeAssessment';
import { ResolvedEntitlementFeaturesItem, UpgradeStage } from 'api/models';
import { getMostRelevanSubscription } from 'utils/subscription_plan';
import FeatureAccessDialog from 'components/dialogs/FeatureAccessDialog';
import { openUpgradeExportInNewWindow } from '../helpers/export_full';
import MarkUpgradeAssessmentAsArchived from '../components/MarkUpgradeAssessmentAsArchived';
import { Stack } from 'components/utils';
import MessageBox from 'components/MessageBox';
import { useExampleData } from 'hooks/useExampleData';

export default function UpgradeAssessmentView() {
  const { logEvent } = AnalyticsEventLogger();
  const { assessmentId } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const { account, currentOrganization } = useUserAccountState();
  const basePath = useAccountIdRoute(
    '/orgs/:orgId/accounts/:accountId/upgrades/assessments',
  );
  const templatesBasePath = useAccountIdRoute(
    '/orgs/:orgId/accounts/:accountId/upgrades/templates/clusters',
  );
  const navigate = useNavigate();
  const { mutate: globalMutate } = useSWRConfig();

  // Check if the currently open Upgrade Plan is an example or real
  const exampleData = assessmentId !== undefined && isExampleData(assessmentId);
  const [showExampleData, setShowExampleData] = useExampleData();
  // update view in localStorage if it doesn't match the currently open Upgrade Plan
  if (exampleData !== showExampleData) {
    setShowExampleData(exampleData);
  }

  //Subscriptions
  const { data: Subscriptions } = useListSubscriptions(
    currentOrganization?.slug,
    {},
    {
      request: { headers: { Authorization: `Bearer ${account?.token}` } },
    },
  );
  const currentSubscription = getMostRelevanSubscription(
    Subscriptions?.subscriptions,
  );
  const showTemplateLock = Subscriptions
    ? !Subscriptions?.total_entitlement?.features?.includes(
        'request_upgrade_template',
      )
    : true;

  const {
    data: upgradeAssessmentData,
    error: upgradeAssessmentError,
    mutate: mutateUpgradeAssessment,
  } = useGetUpgradeAssessment(assessmentId || '', {
    request: { headers: { Authorization: `Bearer ${account.token}` } },
    swr: {
      enabled: !exampleData,
    },
  });
  const {
    data: upgradeAssessmentStagesData,
    error: upgradeAssessmentStagesError,
  } = useListUpgradeAssessmentStages(
    assessmentId || '',
    { n: 100 },
    {
      request: { headers: { Authorization: `Bearer ${account.token}` } },
      swr: {
        enabled: !exampleData,
      },
    },
  );

  //redirect to the upgrade assessments page if there is an error in displaying stage data
  if (upgradeAssessmentData && upgradeAssessmentStagesError) {
    navigate(basePath);
    if (
      upgradeAssessmentStagesError?.response?.status === 400 &&
      (upgradeAssessmentData?.status === 'pending' ||
        upgradeAssessmentData?.status === 'in-progress')
    ) {
      enqueueSnackbar(
        'An Upgrade Assessment is being generated for you. You will receive a notification when the Assessment is ready',
        {
          variant: 'warning',
          autoHideDuration: toastAutoHideDuration,
        },
      );
    } else {
      enqueueSnackbar('No data available for the Upgrade Assessment', {
        variant: 'error',
        autoHideDuration: toastAutoHideDuration,
      });
    }
  }

  const upgradeAssessment: UpgradeAssessment | undefined = exampleData
    ? UPGRADE_ASSESSMENTS.find((u) => u.id === assessmentId)
    : upgradeAssessmentData;
  const upgradeAssessmentStages: UpgradeStage[] | undefined = exampleData
    ? UPGRADE_STAGES.get(assessmentId || '')
    : upgradeAssessmentStagesData?.data;

  const [openEditNameDialog, setOpenEditNameDialog] = useState(false);
  const [
    openMarkUpgradeAssessmentAsArchivedDialog,
    setOpenMarkUpgradeAssessmentAsArchivedDialog,
  ] = useState(false);
  const [openUpgradeSubscriptionDialog, setOpenUpgradeSubscriptionDialog] =
    useState(false);

  if (
    upgradeAssessment === undefined ||
    upgradeAssessmentStages === undefined
  ) {
    return (
      <Page title={'Upgrade Details'}>
        <div
          className={styles['pageContents']}
          style={{ marginLeft: '32px', width: 'calc(100% - 64px)' }}
        >
          <Breadcrumbs>
            <Breadcrumbs.Item>Upgrade Copilot</Breadcrumbs.Item>
            <Breadcrumbs.Divider name="FeatherChevronRight" />
            <Breadcrumbs.Item>Upgrade Assessments</Breadcrumbs.Item>
            <Breadcrumbs.Divider name="FeatherChevronRight" />
            <Breadcrumbs.Item active={true}>
              {upgradeAssessment?.name || assessmentId}
            </Breadcrumbs.Item>
          </Breadcrumbs>
          {upgradeAssessmentError || upgradeAssessmentStagesError ? (
            <Stack className="w-full h-full justify-center">
              <MessageBox
                mode={'error'}
                message={
                  upgradeAssessmentError?.response?.status === 404
                    ? 'Upgrade Assessment not found'
                    : 'An error occurred while fetching the Upgrade Assessment'
                }
              />
            </Stack>
          ) : (
            <ConsoleLoader />
          )}
        </div>
      </Page>
    );
  } else if (
    !exampleData &&
    (upgradeAssessmentError !== undefined ||
      upgradeAssessmentStagesError !== undefined)
  ) {
    return (
      <Page title={'Upgrade Details'}>
        <div
          className={styles['pageContents']}
          style={{ marginLeft: '32px', width: 'calc(100% - 32px)' }}
        >
          <Breadcrumbs>
            <Breadcrumbs.Item>Upgrade Copilot</Breadcrumbs.Item>
            <Breadcrumbs.Divider name="FeatherChevronRight" />
            <Breadcrumbs.Item>Upgrade Assessments</Breadcrumbs.Item>
            <Breadcrumbs.Divider name="FeatherChevronRight" />
            <SkeletonText className="w-[200px]" />
            <Breadcrumbs.Divider name="FeatherChevronRight" />
            <Breadcrumbs.Item active={true}>
              {upgradeAssessment?.name || assessmentId}
            </Breadcrumbs.Item>
          </Breadcrumbs>
          <>Upgrade Assessment ID not found</>
        </div>
      </Page>
    );
  } else {
    const markdownExports = (upgradeAssessment?.exports || []).filter(
      (e) => e.format === 'markdown',
    );
    // TODO(stefan): re-enable when PDF support works
    // const pdfExports = (upgradeAssessment?.exports || []).filter(
    //   (e) => e.format === 'pdf',
    // );

    const actions = (
      <>
        <Actions
          size="large"
          sections={[
            {
              title: 'Update Properties',
              items: [
                <Actions.Item
                  key="edit-name"
                  title="Edit Name"
                  icon="FeatherEdit2"
                  onClick={() => {
                    setOpenEditNameDialog(true);
                  }}
                  disabled={showExampleData}
                  tooltip={
                    showExampleData
                      ? 'Example Upgrade Assessments cannot be updated'
                      : ''
                  }
                />,
              ],
            },
            {
              title: 'Export',
              items: [
                <Actions.Item
                  key="export-as-markdown"
                  title="Export as Markdown"
                  icon="FeatherCode2"
                  onClick={() => {
                    openUpgradeExportInNewWindow(
                      UpgradeViewMode.Assessment,
                      upgradeAssessment.id,
                      account.token,
                      enqueueSnackbar,
                    );
                  }}
                  disabled={
                    (showExampleData &&
                      upgradeAssessment?.id !==
                        'upgrasses_2e6323a4-576b-4d32-99da-70204ee00981' &&
                      upgradeAssessment?.id !==
                        'upgrasses_c4aa47e1-f865-46d4-8b2d-3a15165e6392') ||
                    upgradeAssessment?.status === 'pending' ||
                    upgradeAssessment?.status === 'in-progress' ||
                    markdownExports.length < 1
                  }
                  tooltip={
                    showExampleData &&
                    upgradeAssessment?.id !==
                      'upgrasses_2e6323a4-576b-4d32-99da-70204ee00981' &&
                    upgradeAssessment?.id !==
                      'upgrasses_c4aa47e1-f865-46d4-8b2d-3a15165e6392'
                      ? 'Example Upgrade Assessments cannot be exported'
                      : upgradeAssessment?.status === 'pending' ||
                        upgradeAssessment?.status === 'in-progress' ||
                        markdownExports.length < 1
                      ? 'Action is not available for this Upgrade Assessment'
                      : ''
                  }
                />,
                // <Actions.Item
                //   key="export-as-pdf"
                //   title="Export as PDF"
                //   icon="FeatherFileText"
                //   onClick={() => {
                //     window.open(
                //       pdfExports[0].url,
                //       '_blank',
                //       'noopener,noreferrer',
                //     );
                //   }}
                //   disabled={
                //     (showExampleData &&
                //       upgradeAssessment?.id !== 'upgrasses_2e6323a4-576b-4d32-99da-70204ee00981' &&
                //        upgradeAssessment?.id !== 'upgrasses_c4aa47e1-f865-46d4-8b2d-3a15165e6392') ||
                //     upgradeAssessment?.status === 'pending' ||
                //     upgradeAssessment?.status === 'in-progress' ||
                //     pdfExports.length < 1
                //   }
                //   tooltip={
                //     showExampleData &&
                //     upgradeAssessment?.id !== 'upgrasses_2e6323a4-576b-4d32-99da-70204ee00981'
                //     && upgradeAssessment?.id !== 'upgrasses_c4aa47e1-f865-46d4-8b2d-3a15165e6392'
                //       ? 'Example Upgrade Assessments cannot be exported'
                //       : upgradeAssessment?.status === 'pending' ||
                //         upgradeAssessment?.status === 'in-progress' ||
                //         pdfExports.length < 1
                //       ? 'Action is not available for this Upgrade Assessment'
                //       : ''
                //   }
                // />,
              ],
            },
            {
              title: 'Lifecycle Actions',
              items: [
                <Actions.Item
                  key="request-template"
                  title="Request Upgrade Template"
                  icon="FeatherPlus"
                  onClick={(event) => {
                    if (showTemplateLock && !showExampleData) {
                      event.stopPropagation();
                      event.preventDefault();
                      setOpenUpgradeSubscriptionDialog(true);
                    } else {
                      let filter = '';
                      if (upgradeAssessment?.resources.clusters?.at(0)) {
                        filter = `?cluster_id=${
                          (
                            upgradeAssessment?.resources.clusters?.at(0) || {
                              id: '',
                            }
                          ).id
                        }`;
                      }
                      navigate(`${templatesBasePath}/new${filter}`);
                    }
                  }}
                  disabled={showExampleData}
                  tooltip={
                    showExampleData
                      ? 'Example Upgrade Assessments cannot be used to create Upgrade Templates'
                      : ''
                  }
                  subscriptionLockIcon={showTemplateLock}
                />,
                <Actions.Item
                  key="mark-archived"
                  title="Archive"
                  icon="FeatherArchive"
                  onClick={() => {
                    setOpenMarkUpgradeAssessmentAsArchivedDialog(true);
                  }}
                  disabled={
                    showExampleData || upgradeAssessment.status !== 'ready'
                  }
                  tooltip={
                    showExampleData
                      ? 'Action is not available on example data'
                      : upgradeAssessment.status !== 'ready'
                      ? 'Action is only available for Available Upgrade Assessments'
                      : ''
                  }
                />,
              ],
            },
          ]}
        />
      </>
    );

    return (
      <>
        <ChangeNameDialog
          key="edit-name"
          open={openEditNameDialog}
          setOpen={setOpenEditNameDialog}
          mode={UpgradeViewMode.Assessment}
          name={upgradeAssessment?.name || ''}
          onConfirm={async (newName) => {
            if (showExampleData) {
              enqueueSnackbar('Action is not available on example data', {
                variant: 'warning',
                autoHideDuration: toastAutoHideDuration,
              });
            } else {
              try {
                await updateUpgradeAssessment(
                  upgradeAssessment?.id || '',
                  { name: newName },
                  {
                    headers: {
                      Authorization: `Bearer ${account.token}`,
                    },
                  },
                );
                logEvent('upgrades-assessments-name-change', {
                  template: upgradeAssessment?.id || '',
                  name: newName,
                });
                mutateUpgradeAssessment();
                enqueueSnackbar(
                  'Successfully edited the requested properties of the Upgrade Assessment',
                  {
                    variant: 'success',
                    autoHideDuration: toastAutoHideDuration,
                  },
                );
              } catch (err) {
                enqueueSnackbar(
                  somethingWentWrong.replace(
                    '<action>',
                    'updating requested properties of the Upgrade Assessment',
                  ),
                  {
                    variant: 'error',
                    autoHideDuration: toastAutoHideDuration,
                  },
                );
              }
            }
          }}
        />
        <FeatureAccessDialog
          upgradePlanType={'cluster'}
          open={openUpgradeSubscriptionDialog}
          setOpen={setOpenUpgradeSubscriptionDialog}
          currentPlan={currentSubscription?.plan || 'FreeTrial'}
          feature={ResolvedEntitlementFeaturesItem.request_upgrade_template}
        />
        <MarkUpgradeAssessmentAsArchived
          assessmentId={upgradeAssessment.id}
          mutateAssessment={() => {}}
          open={openMarkUpgradeAssessmentAsArchivedDialog}
          onClose={() => setOpenMarkUpgradeAssessmentAsArchivedDialog(false)}
        />

        <UpgradesViewComponent
          actions={actions}
          mode={UpgradeViewMode.Assessment}
          account={account}
          upgradePlan={upgradeAssessment}
          stages={upgradeAssessmentStages}
          navigate={navigate}
          basePath={basePath}
          breadcrumb={''}
          stagesStepsDefaultOpen={true}
          onChange={(mutateStage = true, mutatePlan = false) => {
            mutatePlan && mutateUpgradeAssessment();
            mutateStage &&
              globalMutate((key) => {
                if (Array.isArray(key)) {
                  if (
                    (key[0] as string).startsWith(
                      `/upgrade_assessments/${upgradeAssessment.id}/`,
                    )
                  ) {
                    return true;
                  }
                }
                return false;
              });
          }}
          additionalHeaderElements={
            <>
              <div className={styles['stack-18c91f9b']}>
                <span className={styles['text']}>Requested:</span>
                <span className={classNames(styles['text'], 'ph-no-capture')}>
                  {getHumanReadableDateAndTime(upgradeAssessment.created)}
                </span>
              </div>
            </>
          }
        />
      </>
    );
  }
}
